import React, { useState, useEffect } from "react";
import { ReferenceListUsageView, ReferenceValueView, ReferenceValueSynonymView } from "@/entities/all";
import { Skeleton } from "@/components/ui/skeleton";
import { List, PlusCircle, ArrowDownCircle, ArrowUpCircle, CheckCircle, Archive } from "lucide-react";

import ReferenceListsTable from "../components/references/ReferenceListsTable";
import CreateReferenceListModal from "../components/references/CreateReferenceListModal";
import DeleteReferenceListDialog from "../components/references/DeleteReferenceListDialog";
import PaginatedTable from "../components/ui/paginated-table";
import ReferenceListDetailsSheet from "../components/references/ReferenceListDetailsSheet";
import { exportReferenceLists } from "../utils/excelTemplateGenerator";
import { ReferenceListsImportDialog } from "../components/reference-lists-import";
import { FilterSelect, FilterSelectItem } from "@/components/ui/filter-select";

export default function ReferenceLists() {
  const [lists, setLists] = useState([]);
  const [filteredLists, setFilteredLists] = useState([]);
  const [valueCounts, setValueCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ category: 'all', status: 'all' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [selectedListId, setSelectedListId] = useState(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  useEffect(() => { loadReferenceLists(); }, []);
  useEffect(() => { filterLists(); }, [lists, searchTerm, filters]);

  const loadReferenceLists = async () => {
    setIsLoading(true);
    try {
      const [data, values] = await Promise.all([
        ReferenceListUsageView.list('list_name'),
        ReferenceValueView.list(),
      ]);
      const counts = {};
      values.forEach(value => { counts[value.list_id] = (counts[value.list_id] || 0) + 1; });
      setValueCounts(counts);
      setLists(data);
    } catch (error) {
      console.error('Error loading reference lists:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterLists = () => {
    let filtered = lists;
    if (searchTerm) {
      filtered = filtered.filter(list =>
        list.list_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        list.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filters.category !== 'all') filtered = filtered.filter(list => list.category === filters.category);
    if (filters.status !== 'all') filtered = filtered.filter(list => list.status === filters.status);
    setFilteredLists(filtered);
  };

  const getUniqueCategories = () => [...new Set(lists.map(l => l.category).filter(Boolean))].sort();

  const handleListCreated = () => { loadReferenceLists(); setIsCreateModalOpen(false); };
  const handleRowClick = (list) => { setSelectedListId(list.id); setIsSheetOpen(true); };
  const handleSheetClose = () => { setIsSheetOpen(false); setSelectedListId(null); };
  const handleSheetSaved = () => { loadReferenceLists(); };
  const handleDeleteList = (list) => { setSelectedList(list); setIsDeleteDialogOpen(true); };
  const handleListDeleted = () => { loadReferenceLists(); setIsDeleteDialogOpen(false); setSelectedList(null); };
  const handleImportComplete = () => { loadReferenceLists(); setIsImportDialogOpen(false); };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const [values, synonyms] = await Promise.all([
        ReferenceValueView.list('canonical_value'),
        ReferenceValueSynonymView.list('synonym'),
      ]);
      exportReferenceLists(lists, values, synonyms);
    } catch (error) {
      console.error('Error exporting reference lists:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const statusStats = { Active: 0, 'Under Review': 0, Deprecated: 0 };
  lists.forEach(list => { statusStats[list.status] = (statusStats[list.status] || 0) + 1; });

  return (
    <div data-tour="page-reference-lists" className="pt-5 pb-24 px-4 md:py-[30px] md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5">

      {/* Title + Actions */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[32px] font-medium leading-tight text-page-title">Reference Lists</h1>
          <p className="text-sm font-normal text-page-muted">
            Manage controlled vocabularies and standardized value lists
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setIsImportDialogOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors"
          >
            <ArrowUpCircle size={24} />
            Import
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || lists.length === 0}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownCircle size={24} />
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 w-[183px] h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors"
          >
            <PlusCircle size={24} />
            New List
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <List size={24} color="#377AD6" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-page-text">Total Lists</span>
            <span className="text-[30px] font-medium leading-none text-page-text">{lists.length}</span>
          </div>
        </div>
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <CheckCircle size={24} color="rgba(77,179,0,0.9)" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-page-text">Active</span>
            <span className="text-[30px] font-medium leading-none text-page-text">{statusStats.Active}</span>
          </div>
        </div>
        <div className="bg-card-bg rounded-[5px] h-[83px] px-5 py-3 flex items-start gap-3">
          <Archive size={24} color="rgba(222,111,107,0.9)" className="shrink-0" />
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-page-text">Deprecated</span>
            <span className="text-[30px] font-medium leading-none text-page-text">{statusStats.Deprecated}</span>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px] flex items-center h-9 bg-card-bg rounded-[18px] px-3 gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M21 21L17.5001 17.5M20 11.5C20 16.1944 16.1944 20 11.5 20C6.80558 20 3 16.1944 3 11.5C3 6.80558 6.80558 3 11.5 3C16.1944 3 20 6.80558 20 11.5Z" stroke="#377AD6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            placeholder="Search reference lists..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 bg-transparent border-0 outline-none text-sm text-page-text placeholder:text-page-muted"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0 max-w-full">
          <FilterSelect value={filters.category} onValueChange={(v) => setFilters(f => ({ ...f, category: v }))} placeholder="Category">
            <FilterSelectItem value="all">All Categories</FilterSelectItem>
            {getUniqueCategories().map(cat => (
              <FilterSelectItem key={cat} value={cat}>{cat}</FilterSelectItem>
            ))}
          </FilterSelect>
          <FilterSelect value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))} placeholder="Status">
            <FilterSelectItem value="all">All Status</FilterSelectItem>
            <FilterSelectItem value="Active">Active</FilterSelectItem>
            <FilterSelectItem value="Under Review">Under Review</FilterSelectItem>
            <FilterSelectItem value="Deprecated">Deprecated</FilterSelectItem>
          </FilterSelect>
        </div>
      </div>

      {/* Table */}
      <PaginatedTable
        data={filteredLists}
        externalLoading={isLoading}
        renderTable={(paginatedData, loading) => (
          <div className="bg-card-bg rounded-[8px] overflow-x-auto">
            <ReferenceListsTable
              lists={paginatedData}
              isLoading={loading}
              onDeleteList={handleDeleteList}
              onRowClick={handleRowClick}
              valueCounts={valueCounts}
            />
          </div>
        )}
      />

      <CreateReferenceListModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onListCreated={handleListCreated}
      />

      {selectedList && (
        <DeleteReferenceListDialog
          isOpen={isDeleteDialogOpen}
          onClose={() => { setIsDeleteDialogOpen(false); setSelectedList(null); }}
          onListDeleted={handleListDeleted}
          list={selectedList}
        />
      )}

      <ReferenceListsImportDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        onImportComplete={handleImportComplete}
      />

      <ReferenceListDetailsSheet
        listId={selectedListId}
        isOpen={isSheetOpen}
        onClose={handleSheetClose}
        onSaved={handleSheetSaved}
      />
    </div>
  );
}
